import { SardineContext } from '@sardine-ai/react-js-wrapper';
import Cookies from 'js-cookie';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import useUpdateEffect from 'react/shared/hooks/UseUpdateEffect';
import api from 'react/signups/card/api.js';
import trackUserData from 'react/signups/card/common/MarketoTracker';
import Client from 'react/signups/card/modules/credentials/create/client/Client';
import Family from 'react/signups/card/modules/credentials/create/family/Family';
import Myself from 'react/signups/card/modules/credentials/create/myself/Myself';
import { USER_TYPES } from 'react/signups/card/recipes/constants';
import { goToNextStep } from 'react/signups/card/routing/navigator';
import cardAccountSignupShape from 'react/signups/card/shapes/CardAccountSignup';
import recipeShape from 'react/signups/card/shapes/Recipe';
import userShape from 'react/signups/card/shapes/User';

export default function Credentials({
  cardAccountSignup,
  recipe,
  setUser,
  showOnboardingBeacon,
  user,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const userType = cardAccountSignup.userType;
  const { updateSardineConfig } = useContext(SardineContext);
  const sardineSessionKey = Cookies.get('sardine_onboarding_session_key');

  // State hooks ------------------------------------------------

  // module navigation state
  const [moduleComplete, setModuleComplete] = useState(false);

  // form data state
  const [credentials, setCredentials] = useState({
    email: user.email || cardAccountSignup.email || '',
    password: '',
    passwordConfirmation: '',
  });

  // Effect hooks and memoized callbacks ------------------------

  // Move to next step or mark module complete
  const onNavigate = useCallback(() => {
    goToNextStep({
      cardAccountSignupId: cardAccountSignup.id,
      location,
      navigate,
      recipe,
      moduleComplete,
      setModuleComplete,
      userType,
    });
  }, [
    cardAccountSignup.id,
    location,
    navigate,
    moduleComplete,
    setModuleComplete,
    recipe,
    userType,
  ]);

  // Send module payload and update Application-level state
  useUpdateEffect(() => {
    if (!moduleComplete || user.email) return;

    const params = {
      card_account_signup_id: cardAccountSignup.id,
      user: {
        email: credentials.email,
        password: credentials.password,
        password_confirmation: credentials.passwordConfirmation,
      },
    };

    api
      .createUser(params)
      .then((response) => {
        const organization = response.data.organization.data;
        trackUserData({
          Expert_Type__c: organization.attributes.customerType,
        });

        const { attributes, id } = response.data.user.data;
        setUser({ id, ...attributes });

        //send user ID to GTM
        if ($tlf.environment === 'production') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ userId: id });
        }

        updateSardineConfig({
          customerId: id, // This is user.slug
          flow: 'onboarding',
          sessionKey: sardineSessionKey,
        });
      })
      .catch(() => {
        setModuleComplete(false);
      });
  }, [moduleComplete]);

  // navigate to next step when Module-level state changes in response to form submit
  useUpdateEffect(() => {
    onNavigate();
  }, [credentials]);

  // Navigate to next module when Application-level state changes in response to api success,
  // or prevent revisiting this step if user already exists
  useEffect(() => {
    if (user.email) {
      onNavigate();
    }
  }, [user, onNavigate]);

  useEffect(() => {
    if (userType === USER_TYPES.MYSELF) recipe.defaultPath = '/account/create_self';
    if (userType === USER_TYPES.CLIENT) recipe.defaultPath = '/account/create_professional';
    if (userType === USER_TYPES.FAMILY) recipe.defaultPath = '/account/create_family';
  }, [userType, recipe]);

  // Pass-down state, props and handlers ------------------------------

  const passDownValues = {
    cardholderFirstName: cardAccountSignup.cardholderFirstName || '',
    credentials,
    moduleComplete,
    showOnboardingBeacon,
  };

  const passDownHandlers = {
    setCredentials,
  };

  return (
    <Routes>
      <Route
        element={<Client {...passDownValues} {...passDownHandlers} />}
        path="/create_professional"
      />
      <Route element={<Myself {...passDownValues} {...passDownHandlers} />} path="/create_self" />
      <Route element={<Family {...passDownValues} {...passDownHandlers} />} path="/create_family" />
      <Route element={<Navigate replace to={recipe.defaultPath} />} path="/*" />
    </Routes>
  );
}

Credentials.propTypes = {
  cardAccountSignup: cardAccountSignupShape,
  recipe: recipeShape.isRequired,
  setUser: PropTypes.func.isRequired,
  showOnboardingBeacon: PropTypes.bool,
  user: userShape,
};
